// ISDP CUSTOM — i18n "admin" namespace for the admin plugin pages.
//
// Uses Backstage's official translation system (createTranslationRef + useTranslationRef,
// language driven by appLanguageApiRef, wired globally in packages/app/src/App.tsx with
// availableLanguages ['en','de']). This ref is decentralized: it self-registers when a
// component in this plugin reads it via useTranslationRef, so no app-side wiring is needed.
//   - English is the source of truth and lives inline in `messages` (nested; keys flatten with dots).
//   - German is lazy-imported from ./de/admin — a flat map of "dotted.key" -> string.
// Components read strings with:  const { t } = useTranslationRef(adminMessages); t('access.title')
import { createTranslationRef } from '@backstage/core-plugin-api/alpha';

export const adminMessages = createTranslationRef({
  id: 'devhub.pluginAdmin',
  messages: {
    // Shared action words reused across the admin pages.
    common: {
      remove: 'Remove',
      delete: 'Delete',
      cancel: 'Cancel',
      edit: 'Edit',
      copy: 'Copy',
      copied: 'Copied!',
      loading: 'Loading…',
      backToAdmin: 'Back to Admin',
      dismiss: 'Dismiss',
      networkError: 'Network error',
      retry: 'Retry',
      // Small uppercase kicker above hero titles across the admin area.
      adminEyebrow: 'Admin',
    },

    // Tab labels for the consolidated Analytics and Curation hub pages.
    hub: {
      analyticsAria: 'Analytics views',
      curationAria: 'Curation views',
      analyticsTabs: {
        impact: 'Impact',
        usage: 'Usage',
        ai: 'AI Usage',
      },
      curationTabs: {
        content: 'Content',
        radar: 'Tech Radar',
        mcp: 'MCP',
      },
      // Insights hero (AnalyticsPage) — the section title + supporting copy.
      insightsTitle: 'Insights',
      insightsIntro:
        'Adoption, usage and AI-cost signals for the DevHub — anonymous, aggregate, and refreshed on demand.',
    },

    // AdminPage.tsx — dashboard
    dashboard: {
      heroTitle: 'Admin Dashboard',
      heroSubtitle: 'DevHub Role-Based Access Control',
      currentUser: 'Current User',
      userEntity: 'User entity:',
      detectedRole: 'Detected role:',
      groupMemberships: 'Group memberships:',
      noGroups: 'No groups assigned',
      allOwnershipRefs: 'All ownership refs:',
      role: {
        admin: 'Admin',
        internal: 'Dräger Internal',
        external: 'External',
        unknown: 'Unknown',
      },
      catalogStatus: 'Catalog Status',
      entityOne: 'entity',
      entityOther: 'entities',
      catalogEmpty:
        'Catalog is empty. The Azure DevOps provider may not be discovering repos yet. Check that AZURE_DEVOPS_TOKEN is set.',
      refreshAll: 'Refresh All Entities',
      refreshing: 'Refreshing...',
      reloadStats: 'Reload Stats',
      progressEntities: '{{done}} / {{total}} entities',
      refreshDone:
        'Done — {{refreshed}} refreshed{{skipped}}. Reload page in a moment to see updates.',
      refreshSkipped: ', {{n}} skipped',
      refreshError: 'Error: {{message}}',
      visibilityAnnotation: 'Visibility Annotation',
      visibilityIntro:
        'Entities are tagged with {{code}} to control which roles can see them:',
      visibilityExternal: 'visible to everyone (Admin + Internal + External)',
      visibilityInternal:
        'visible to Admin + Internal only (default when no annotation is set)',
      visibilityAdmin: 'visible to Admin only',
      permissionMatrix: 'Role Permission Matrix',
      colResource: 'Resource / Action',
      colAdmin: 'Admin',
      colInternal: 'Internal',
      colPartner: 'Registered partner',
      colPublic: 'Public',
      matrix: {
        adminOnly: 'Catalog: admin-only entities',
        internalDefault: 'Catalog: internal entities (default)',
        externalEntities: 'Catalog: external entities',
        premiumEntities: 'Catalog: Premium entities (per product)',
        previewDrafts: 'Catalog: preview drafts',
        catalogEdit: 'Catalog: create / update / refresh',
        deleteActions: 'Delete actions (catalog and plugins)',
        adminPage: 'Admin page',
        landscape: 'Landscape',
        scaffolder: 'Scaffolder / TechDocs / Search and other plugins',
      },
      cell: {
        visible: 'Visible',
        hidden: 'Hidden',
        allowed: 'Allowed',
        denied: 'Denied',
        perGrant: 'Per Premium grant',
      },
      tierManagement: 'Tier Management',
      tierLoading: 'Loading tier assignments...',
      tierEmpty:
        'No tier assignments yet. External users default to Basic tier. Assign tiers when external users sign in.',
      tierColUser: 'User',
      tierColTier: 'Tier',
      tierColUpdated: 'Updated',
      tierColActions: 'Actions',
      tierBasic: 'Basic',
      tierPartner: 'Partner',
      tierEnterprise: 'Enterprise',
      tierAriaLabel: 'Tier',
    },

    // AccessManagementPage.tsx (+ AccessPanels / ProductPicker)
    access: {
      eyebrow: 'Admin · Access Management',
      title: 'Roles & Access',
      intro:
        "Grant DevHub roles and per-product Premium access by email address. Assignments are stored in the database and applied at the user's next sign-in. Users with a {{draeger}} address default to {{devLabel}}; everyone else defaults to {{publicLabel}} until assigned.",
      roleLabel: {
        public: 'Public',
        partner: 'Registered partner',
        draeger: 'Internal',
        admin: 'Admin',
      },
      roleDesc: {
        public:
          'Public partner portal only — no internal access and no Premium product content.',
        partner:
          'Signed-in partner portal. Registered content for every product, plus Premium content for each product granted below.',
        draeger:
          'Dräger employee: the full internal developer hub plus all partner and product content.',
        admin: 'Everything Internal can do, plus the admin management surfaces.',
      },
      email: 'Email',
      emailPlaceholder: 'person@example.com',
      role: 'Role',
      saving: 'Saving…',
      save: 'Save',
      addUser: 'Add user',
      addUserTitle: 'Add a user',
      addUserHint:
        'New users start as a Registered partner with no Premium products.',
      currentAssignments: 'Users',
      noAssignments: 'No assignments yet.',
      noMatches: 'No users match these filters.',
      colEmail: 'Email',
      colCompany: 'Company',
      colRole: 'Role',
      colProducts: 'Premium products',
      colUpdated: 'Updated',
      premiumProducts: 'Premium products',
      allProducts: 'All products',
      registeredOnly: 'Registered only',
      searchLabel: 'Search users',
      searchPlaceholder: 'Search by email or company…',
      filterRole: 'Filter by role',
      filterFamily: 'Filter by product family',
      filterProduct: 'Filter by Premium product',
      filterAllRoles: 'All roles',
      filterAllFamilies: 'All families',
      filterAllProducts: 'All products',
      clearFilters: 'Clear filters',
      selectPage: 'Select all users on this page',
      selectRow: 'Select {{email}}',
      pageRange: '{{from}}–{{to}} of {{total}}',
      pageOf: 'Page {{page}} of {{pages}}',
      prevPage: 'Previous page',
      nextPage: 'Next page',
      closePanel: 'Close panel',
      updatedLine: 'updated by {{by}}, {{at}}',
      presets: 'Quick select',
      preset: {
        infinity: 'All Infinity',
        'interop-suite': 'All Interop Suite',
        everything: 'Everything',
        none: 'None',
      },
      selectAllIn: 'Select all {{family}} products',
      familyCount: '{{n}} of {{total}}',
      selectedCount: '{{n}} selected',
      status: {
        draft: 'Draft',
        parked: 'Parked',
      },
      parkedHint: 'Parked: this product can’t be newly granted.',
      unknownGroup: 'Unknown products',
      unknownHint:
        'These stored grants match no product in the current product list. Deselect them to remove them on save.',
      unknownProduct: 'Unknown product',
      noProducts: 'No products available.',
      productsLoadErr:
        "Couldn't load the product list, so Premium grants can't be edited right now.",
      noPremiumForPublic: 'Public users have no Premium products.',
      allProductsForInternal: '{{role}} users see all products through their role.',
      selectionKept:
        'The {{n}} selected Premium product(s) are kept in case you switch back to Registered partner.',
      discardTitle: 'Discard unsaved changes?',
      discardBody: 'Your changes to this user haven’t been saved.',
      discard: 'Discard',
      keepEditing: 'Keep editing',
      overwriteTitle: 'Replace the existing assignment?',
      overwriteBody: '{{email}} already has a role. Saving replaces their role and Premium products.',
      overwrite: 'Replace',
      removeAssignment: 'Remove assignment',
      removeTitle: 'Remove this role assignment?',
      removeBody:
        '{{email}} falls back to the default role (Internal for @draeger.com, otherwise Public) at their next sign-in.',
      removed: 'Removed the assignment for {{email}}',
      roleTakesEffect:
        '{{email}} → {{label}}. Takes effect on their next login.',
      droppedProducts:
        'Saved, but the server did not accept these products: {{ids}}',
      errEnterEmail: 'Enter an email address.',
      errValidEmail: 'Enter a valid email address.',
      errSetRole: 'Failed to set role.',
      errSaveFor: "Couldn't save {{email}}.",
      errRemove: 'Failed to remove assignment.',
      loadErr: "Couldn't load role assignments.",
      protected: 'Protected',
      protectedHint:
        'Built-in super-admin — this account is always an admin and can’t be changed or removed here.',
      bulk: {
        toolbar: 'Bulk actions',
        selected: '{{n}} selected',
        grant: 'Grant products',
        revoke: 'Revoke products',
        setRole: 'Set role',
        clear: 'Clear selection',
        grantTitle: 'Grant Premium products',
        revokeTitle: 'Revoke Premium products',
        setRoleTitle: 'Set role',
        grantHint:
          'Adds the chosen products to every selected Registered partner. Other roles are skipped.',
        revokeHint:
          'Removes the chosen products from every selected Registered partner. Other roles are skipped.',
        setRoleHint:
          'Each user keeps their Premium product selection, so switching back to Registered partner restores it.',
        appliesTo: '{{n}} users selected',
        apply: 'Apply to {{n}}',
        confirmBody: 'This changes {{n}} users. Changes take effect on their next login.',
        progress: '{{done}} of {{total}} saved',
        skipped: '{{n}} skipped (protected, not a Registered partner, or already up to date).',
        allOk: 'All changes saved.',
        done: 'Done',
        toastOk: 'Updated {{n}} users',
        toastErr: 'Updated {{ok}} users; {{failed}} failed',
      },
    },

    // CurationPage.tsx
    curation: {
      back: 'Back to Admin',
      title: 'Curated Content',
      subtitle:
        'Control which entities appear on the Curated Docs and Landscape pages',
      docsTitle: 'Curated Docs',
      docsDesc:
        'Entities shown on the Curated Documentation page. Search for TechDocs entities to add.',
      docsSearchPlaceholder: 'Search TechDocs entities to add...',
      docsEmpty: 'No curated docs yet. Search above to add some.',
      docsEmptyHint:
        'No curated docs yet — start typing in the search box above to find TechDocs entities to add.',
      landscapeEmptyHint:
        'No approved landscape entities yet — start typing in the search box above to find catalog entities to add, or use “Approve All”.',
      loadErrDocs: "Couldn't load curated docs.",
      loadErrLandscape: "Couldn't load the approved landscape.",
      docsCountOne: '{{n}} curated doc',
      docsCountOther: '{{n}} curated docs',
      landscapeTitle: 'Approved Landscape',
      landscapeDesc:
        "Controls which entities appear on the Landscape page, and where on the landscape each one sits. Until you approve any, the landscape shows all entities; once you approve some, only those are shown. Set each entity's {{category}} / {{subcategory}} and tile size below — changes save immediately. The {{category}} is the shared DevHub taxonomy: it picks the landscape section AND the entity's option in the All Documentation category filter, so it can only be chosen from the fixed list.",
      categoryWord: 'Category',
      subcategoryWord: 'Subcategory',
      landscapeSearchPlaceholder: 'Search landscape entities to add...',
      noFilterActive:
        'No approval filter active — all {{n}} landscape entities are currently visible. Click "Approve All" to activate curation, then remove individual entities to hide them.',
      approveAll: 'Approve All ({{n}} entities)',
      approvedCount:
        '{{approved}} approved of {{total}} total landscape entities',
      approveRemaining: 'Approve Remaining ({{n}})',
      noLandscapeEntities:
        'No entities available for the landscape — the catalog is empty apart from Location, User and Group entities.',
      categoryNone: 'No category',
      categoryRetired: '{{value}} (retired)',
      placeSubcategory: 'Subcategory',
      placeCategoryTitle:
        'Shared DevHub category — drives the landscape section and the All Documentation filter',
      placeSubcategoryTitle: 'Sub-group within the section',
      tileSizeAria: 'Tile size',
      sizeSmall: 'Small',
      sizeLarge: 'Large',
      errAdd: 'Failed to add ({{status}})',
      errRemove: 'Failed to remove ({{status}})',
      errUpdate: 'Failed to update ({{status}})',
      errBulk: '{{n}} entities failed to add{{detail}}',
    },

    // TechRadarCurationPage.tsx
    radar: {
      eyebrow: 'Admin · Tech Radar',
      title: 'Tech Radar Curation',
      introBeforeLink:
        'Curate the technology radar entries. Move technologies between rings (Adopt / Trial / Assess / Hold) and quadrants, record movement, and add or retire entries. Changes are live immediately on the ',
      introAfterLink: '.',
      linkText: 'Tech Radar',
      editHeading: 'Edit “{{title}}”',
      addHeading: 'Add an entry',
      titleLabel: 'Title',
      titlePlaceholder: 'e.g. Deno',
      quadrant: 'Quadrant',
      ring: 'Ring',
      movement: 'Movement',
      date: 'Date',
      description: 'Description',
      descriptionPlaceholder: 'Why is it in this ring?',
      saving: 'Saving…',
      saveChanges: 'Save changes',
      addEntry: 'Add entry',
      filterPlaceholder: 'Filter entries by name, ring, or description…',
      filterAria: 'Filter tech radar entries',
      matchOne: '{{n}} match',
      matchOther: '{{n}} matches',
      noMatch: 'No entries match “{{query}}”.',
      colNum: '#',
      colTitle: 'Title',
      colRing: 'Ring',
      colMove: 'Move',
      colDescription: 'Description',
      noEntries: 'No entries.',
      errTitleRequired: 'Title is required.',
      errSave: 'Failed to save entry.',
      loadErr: "Couldn't load the tech radar entries.",
      movedIn: '▲ Moved in',
      movedNone: '— No change',
      movedOut: '▼ Moved out',
      quadrants: {
        languages: 'Languages & Frameworks',
        infrastructure: 'Infrastructure',
        datastores: 'Data Stores',
        techniques: 'Techniques',
      },
    },

    // McpCatalogAdminPage.tsx
    mcp: {
      eyebrow: 'Admin · MCP Catalog',
      title: 'MCP Marketplace',
      // Intro paragraph is split around an inline <code> element.
      intro1: 'MCP servers are ',
      introRepoFed: 'repo-fed',
      serversHeading: 'MCP servers',
      repoFedTag: '· repo-fed',
      serversHelp1: 'To add a server, create a repo with a ',
      serversHelp2: ' (kind ',
      serversHelp3: ') in the ',
      serversHelp4: ' project.',
      noServers: 'No server listings discovered yet.',
      colName: 'Name',
      colTeam: 'Team',
      colTags: 'Tags',
      colVisibility: 'Visibility',
      colVerified: 'Verified',
      visEveryone: 'Everyone',
      visInternal: 'Internal',
      // Verify / unverify controls (reuse the 'mcp' curation context).
      verify: 'Verify',
      unverify: 'Unverify',
      verifiedLabel: 'Verified',
      unverifiedLabel: 'Not verified',
      verified: 'Server marked verified.',
      unverified: 'Server unverified.',
      errVerify: "Couldn't update the verified state.",
      serviceKeys: 'Service keys',
      // Help paragraph split around inline <strong> elements.
      serviceKeysHelp1: 'For ',
      serviceKeysGaia: 'GAIA / automation',
      serviceKeysHelp2: ' — these keys grant ',
      serviceKeysFullRead: 'full read',
      serviceKeysHelp3:
        " access to the MCP server (not scoped to a single user's entitlements). Treat them as secrets and rotate regularly.",
      keyName: 'Key name',
      keyNamePlaceholder: 'e.g. GAIA production',
      creating: 'Creating…',
      createServiceKey: 'Create service key',
      keyRevealHeading: 'Service key created — copy it now',
      keyRevealText1: 'This is the ',
      keyRevealOnlyTime: 'only time',
      keyRevealText2:
        ' the full key for "{{name}}" is shown. Store it somewhere safe; if you lose it, revoke it and create a new one.',
      noKeys: 'No service keys yet.',
      colPrefix: 'Prefix',
      colCreated: 'Created',
      colLastUsed: 'Last used',
      revoke: 'Revoke',
      errKeyName: 'Give the service key a name.',
      errMint: 'Failed to mint service key.',
      loadErrServers: "Couldn't load MCP servers.",
      loadErrKeys: "Couldn't load service keys.",
    },

    // UsageAnalyticsPage.tsx
    usage: {
      back: 'Back to Admin',
      title: 'Usage Analytics',
      subtitle:
        'Anonymous, aggregate usage — no personal data is stored or shown.',
      timeRange: 'Time range',
      rangeDays: '{{days}}d',
      loading: 'Loading usage…',
      loadError: 'Couldn’t load analytics: {{error}}',
      errLoad: 'Failed to load analytics',
      errRequestFailed: 'Request failed ({{status}})',
      distinctUsers: 'Distinct users',
      totalEvents: 'Total events',
      pageViews: 'Page views',
      actions: 'Actions',
      activityTitle: 'Activity — last {{days}} days',
      legendEvents: 'Events',
      legendUsers: 'Distinct users',
      chartAria: 'Daily events and distinct users over the selected range',
      topPages: 'Top pages',
      topActions: 'Top actions',
      byRole: 'By role',
      unitPageViews: 'page views',
      unitEvents: 'events',
      barEmpty: 'No {{label}} recorded yet.',
      usersSub: '· {{n}} users',
      topSearches: 'Top searches',
      searchEmpty: 'No searches recorded yet.',
      contentGaps: 'Content gaps (searched, not found)',
      contentGapsHint: 'These terms returned zero results — consider writing docs for them.',
      gapsEmpty: 'No content gaps in this range.',
      colTerm: 'Search term',
      colCount: 'Searches',
      privacy:
        'Users are counted via a one-way salted hash of their identity. The DevHub never stores names, emails, or IP addresses in analytics — only page paths, action labels, and coarse role buckets.',
      role: {
        admin: 'Admin',
        draeger: 'Dräger',
        partner: 'Partner',
        public: 'Public',
      },
    },

    // AiUsagePage.tsx
    aiUsage: {
      back: 'Back to Admin',
      title: 'AI Usage & Cost',
      subtitle:
        'Estimated AI spend and call metering across DevHub features.',
      timeRange: 'Time range',
      rangeDays: '{{days}}d',
      loading: 'Loading AI usage…',
      loadError: 'Couldn’t load AI usage: {{error}}',
      errLoad: 'Failed to load AI usage',
      errRequestFailed: 'Request failed ({{status}})',
      estimateBadge: 'Estimate',
      estimateText:
        'Costs are an ESTIMATE derived from token usage × the configured per-model rates (ai.rates in app-config). Reconcile against the GAIA euro report, which also includes non-DevHub usage.',
      totalEur: 'Est. total cost',
      meteredCalls: 'Metered calls',
      successRate: 'Success rate',
      spendTitle: 'Estimated daily spend — last {{days}} days',
      chartAria: 'Estimated AI spend per day over the selected range',
      byModel: 'By model',
      byFeature: 'By feature',
      noData: 'No AI calls recorded yet.',
      colModel: 'Model',
      colFeature: 'Feature',
      colCalls: 'Calls',
      colInTok: 'Input tok',
      colOutTok: 'Output tok',
      colEur: 'Est. €',
      perMTokIn: 'M in',
      perMTokOut: 'M out',
      ratesLabel: 'Configured rates —',
      testExcluded:
        'Test calls excluded from figures above: {{n}} ({{eur}}).',
    },

    // ScoreboardPage.tsx — the "Impact" tab (adoption at a glance).
    scoreboard: {
      sectionTitle: 'Impact',
      sub: 'Adoption at a glance. Anonymous, aggregate signals over the selected window — no personal data.',
      timeRange: 'Time range',
      rangeLabel: 'Last {{days}} days',
      loading: 'Loading impact…',
      kpiActiveUsers: 'Active users',
      kpiActiveUsersHint: 'last {{days}} days',
      kpiSearches: 'Searches',
      kpiAiAnswers: 'AI answers',
      kpiAiAnswersHint: 'DevHub Assistant + tooling',
      kpiCatalogEntities: 'Catalog entities',
      kpiCatalogEntitiesHint: 'services, tools, templates',
      kpiDocsPublished: 'Docs published',
      kpiDocsPublishedHint: 'with TechDocs',
      kpiContentGaps: 'Content gaps',
      kpiContentGapsHint: 'dead-end searches',
      activityTrend: 'Activity trend',
      interactions: '{{events}} interactions over {{days}} days',
      adoptionByAudience: 'Adoption by audience',
      noSignedInActivity: 'No signed-in activity yet.',
      whatToWriteNext: 'What to write next',
      whatToWriteNextHint: 'top dead-end searches',
      noDeadEnds: 'No dead-end searches — discovery is healthy. 🎉',
      topSearches: 'Top searches',
      topSearchesHint: 'what people look for',
      noSearches: 'No searches yet.',
      role: {
        admin: 'Admins',
        draeger: 'Dräger staff',
        partner: 'Partners',
        public: 'Public',
      },
    },
  },
  translations: {
    de: () => import('./de/admin'),
  },
});
